// Version: 1.4
// v1.4: Phone width: a row of stage chips above the stacked columns, each with its
//       count; tapping one shows only that stage, All shows them all again. Empty
//       columns start collapsed on a phone, so the long list holds only stages with
//       cards. Desktop is unchanged.
// v1.3: Optional selection mode (Pipeline bulk changes, CV-54). With
//       selectable on, each card shows a checkbox and a click selects it
//       instead of opening it, dragging and the mobile Move to menu are off,
//       and each column header has a select-all box. Tickets.js is unchanged.
// v1.2: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size. Panels still drawn dark carry className="surface-dark" so their buttons pick up dark values.
// 1.1: Column bodies use the shared .thin-scroll style so the vertical
//      scrollbar is thin and dark instead of the browser default grey.
// 1.0: Generic left-to-right board used by Pipeline.js (sales stages) and
//      Tickets.js (Unassigned to Closed). Native HTML5 drag and drop on
//      desktop, no library. Columns share the available width so the page
//      never scrolls sideways; each column scrolls vertically on its own.
//      On mobile (useIsMobile, 768px) columns stack as collapsible sections
//      and each card gets a "Move to" dropdown instead of dragging.
//
// Props:
//   columns:     [{ id, title, color, items, subtitle?, collapsedByDefault? }]
//   getId:       item => unique id
//   renderCard:  (item, column) => node (the card body; Board draws the frame)
//   onMove:      (item, fromColumnId, toColumnId) => void | Promise
//                The caller decides what a move means and may open a prompt
//                first (for example choosing an assignee or a lost reason).
//   onOpen:      item => void (card click)
//   isMobile:    boolean
//   canDrop:     (item, fromId, toId) => boolean (optional)
//   emptyText:   string shown in an empty column
//   selectable:  boolean (optional) turns on selection mode
//   selectedIds: Set of ids that are selected
//   onToggleSelect: item => void
//   onToggleColumn: (column, selectAll) => void
import { useState } from "react";

export default function Board({
  columns, getId, renderCard, onMove, onOpen, isMobile,
  canDrop, emptyText = "Nothing here",
  selectable = false, selectedIds, onToggleSelect, onToggleColumn,
}) {
  const isSel = (item) => !!(selectedIds && selectedIds.has(getId(item)));
  const colState = (col) => {
    const n = col.items.filter(isSel).length;
    return { all: n > 0 && n === col.items.length, some: n > 0 && n < col.items.length };
  };
  const clickCard = (item) => {
    if (selectable) { onToggleSelect && onToggleSelect(item); return; }
    onOpen && onOpen(item);
  };
  const [dragging, setDragging] = useState(null);   // { id, from }
  const [overCol, setOverCol] = useState(null);
  const [only, setOnly] = useState(null);   // v1.4: phone, one stage at a time
  const [collapsed, setCollapsed] = useState(() => {
    const init = {};
    columns.forEach(c => { if (c.collapsedByDefault) init[c.id] = true; });
    return init;
  });

  const findItem = (id, colId) => {
    const col = columns.find(c => c.id === colId);
    return col ? col.items.find(i => String(getId(i)) === String(id)) : null;
  };

  const allowed = (item, from, to) => from !== to && (!canDrop || canDrop(item, from, to));

  function handleDrop(e, toId) {
    e.preventDefault();
    setOverCol(null);
    let payload = dragging;
    if (!payload) {
      try { payload = JSON.parse(e.dataTransfer.getData("text/plain")); } catch { payload = null; }
    }
    setDragging(null);
    if (!payload) return;
    const item = findItem(payload.id, payload.from);
    if (item && allowed(item, payload.from, toId)) onMove(item, payload.from, toId);
  }

  // ── Mobile: stacked collapsible columns with a Move to dropdown ────────────
  if (isMobile) {
    const onlyCol = only && columns.some(c => c.id === only) ? only : null;
    const shown = onlyCol ? columns.filter(c => c.id === onlyCol) : columns;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          <button type="button" className="btn btn-secondary btn-sm" aria-pressed={!onlyCol} onClick={() => setOnly(null)}>All</button>
          {columns.map(c => (
            <button type="button" key={c.id} className="btn btn-secondary btn-sm" aria-pressed={onlyCol === c.id}
              onClick={() => setOnly(o => (o === c.id ? null : c.id))}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: c.color || "#86868b", flexShrink: 0 }} />
              {c.title}
              <span style={{ color: "var(--text-muted)" }}>{c.items.length}</span>
            </button>
          ))}
        </div>
        {shown.map(col => {
          const isCollapsed = onlyCol ? false : (col.id in collapsed ? !!collapsed[col.id] : col.items.length === 0);
          return (
            <div className="surface-dark" key={col.id} style={{ background: "#141414", border: "1px solid #2c2c2e", borderRadius: 12, overflow: "hidden" }}>
              <button className="btn-bare"
                onClick={() => setCollapsed(c => ({ ...c, [col.id]: !isCollapsed }))}
                style={{
                  width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 8, padding: "12px 14px",
                  borderBottom: isCollapsed ? "none" : "1px solid #2c2c2e",
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: col.color || "#86868b", flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#f5f5f7" }}>{col.title}</span>
                  <span style={{ fontSize: 12, color: "#86868b" }}>{col.items.length}</span>
                  {selectable && col.items.length > 0 && <ColumnCheck state={colState(col)} onChange={all => onToggleColumn && onToggleColumn(col, all)} />}
                  {col.subtitle && <span style={{ fontSize: 11, color: "#86868b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{col.subtitle}</span>}
                </span>
                <i className={`ti ${isCollapsed ? "ti-chevron-down" : "ti-chevron-up"}`} style={{ color: "#86868b", fontSize: 16 }} />
              </button>
              {!isCollapsed && (
                <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                  {col.items.length === 0 && <div style={{ fontSize: 12, color: "#636366", padding: "6px 4px" }}>{emptyText}</div>}
                  {col.items.map(item => (
                    <div key={getId(item)} style={cardFrame(false, selectable && isSel(item))}>
                      <div onClick={() => clickCard(item)} style={{ cursor: onOpen || selectable ? "pointer" : "default", display: "flex", gap: 8, alignItems: "flex-start" }}>
                        {selectable && <CardCheck on={isSel(item)} />}
                        <div style={{ flex: 1, minWidth: 0 }}>{renderCard(item, col)}</div>
                      </div>
                      {!selectable && <select
                        value={col.id}
                        onChange={e => { const to = e.target.value; if (allowed(item, col.id, to)) onMove(item, col.id, to); }}
                        onClick={e => e.stopPropagation()}
                        style={{
                          marginTop: 8, width: "100%", boxSizing: "border-box", background: "#2c2c2e",
                          border: "1px solid #3a3a3c", borderRadius: 7, padding: "6px 8px",
                          color: "#d1d1d6", fontSize: 12, fontFamily: "inherit",
                        }}
                        aria-label="Move to"
                      >
                        {columns.map(c => (
                          <option key={c.id} value={c.id} disabled={c.id !== col.id && !allowed(item, col.id, c.id)}>
                            {c.id === col.id ? `In ${c.title}` : `Move to ${c.title}`}
                          </option>
                        ))}
                      </select>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ── Desktop: columns side by side, drag and drop ───────────────────────────
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
      gap: 10, alignItems: "start", minWidth: 0,
    }}>
      {columns.map(col => {
        const isOver = overCol === col.id && dragging && dragging.from !== col.id;
        const blocked = isOver && dragging && !allowed(findItem(dragging.id, dragging.from) || {}, dragging.from, col.id);
        return (
          <div
            key={col.id}
            onDragOver={e => { if (dragging) { e.preventDefault(); if (overCol !== col.id) setOverCol(col.id); } }}
            onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setOverCol(o => (o === col.id ? null : o)); }}
            onDrop={e => handleDrop(e, col.id)}
            style={{
              background: isOver ? (blocked ? "rgba(255,69,58,0.06)" : "rgba(0,212,255,0.06)") : "#141414",
              border: `1px solid ${isOver ? (blocked ? "#ff453a" : "#00d4ff") : "#2c2c2e"}`,
              borderRadius: 12, minWidth: 0, display: "flex", flexDirection: "column",
              maxHeight: "calc(100vh - 230px)", minHeight: 160, transition: "background 0.12s, border-color 0.12s",
            }}
          >
            <div style={{ padding: "11px 12px 9px", borderBottom: "1px solid #2c2c2e", minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: col.color || "#86868b", flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: "#f5f5f7", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{col.title}</span>
                <span style={{ fontSize: 11, color: "#86868b", marginLeft: "auto", flexShrink: 0 }}>{col.items.length}</span>
                {selectable && col.items.length > 0 && <ColumnCheck state={colState(col)} onChange={all => onToggleColumn && onToggleColumn(col, all)} />}
              </div>
              {col.subtitle && <div style={{ fontSize: 11, color: "#86868b", marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{col.subtitle}</div>}
            </div>
            <div className="thin-scroll" style={{ padding: 8, display: "flex", flexDirection: "column", gap: 7, overflowY: "auto", minWidth: 0 }}>
              {col.items.length === 0 && (
                <div style={{ fontSize: 12, color: "#636366", padding: "14px 4px", textAlign: "center" }}>{emptyText}</div>
              )}
              {col.items.map(item => {
                const id = getId(item);
                const isDragging = dragging && String(dragging.id) === String(id);
                return (
                  <div
                    key={id}
                    draggable={!selectable}
                    onDragStart={e => {
                      if (selectable) { e.preventDefault(); return; }
                      const payload = { id, from: col.id };
                      setDragging(payload);
                      e.dataTransfer.effectAllowed = "move";
                      try { e.dataTransfer.setData("text/plain", JSON.stringify(payload)); } catch {}
                    }}
                    onDragEnd={() => { setDragging(null); setOverCol(null); }}
                    onClick={() => clickCard(item)}
                    style={{ ...cardFrame(isDragging, selectable && isSel(item)), cursor: selectable ? "pointer" : "grab",
                      display: selectable ? "flex" : "block", gap: 8, alignItems: "flex-start" }}
                  >
                    {selectable && <CardCheck on={isSel(item)} />}
                    {selectable ? <div style={{ flex: 1, minWidth: 0 }}>{renderCard(item, col)}</div> : renderCard(item, col)}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CardCheck({ on }) {
  return (
    <span aria-hidden="true" style={{
      width: 16, height: 16, borderRadius: 4, flexShrink: 0, marginTop: 1, boxSizing: "border-box",
      border: `1.5px solid ${on ? "#00d4ff" : "#636366"}`, background: on ? "#00d4ff" : "transparent",
      display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#000", fontSize: 11, fontWeight: 800,
    }}>{on ? "✓" : ""}</span>
  );
}

function ColumnCheck({ state, onChange }) {
  return (
    <input type="checkbox" checked={state.all} title={state.all ? "Clear this column" : "Select every card in this column"}
      ref={el => { if (el) el.indeterminate = state.some; }}
      onClick={e => e.stopPropagation()}
      onChange={() => onChange(!state.all)}
      style={{ margin: 0, flexShrink: 0, accentColor: "#00d4ff", cursor: "pointer" }} />
  );
}

function cardFrame(isDragging, selected = false) {
  return {
    background: selected ? "rgba(0,212,255,0.08)" : "#1c1c1e", border: `1px solid ${selected ? "#00d4ff" : "#2c2c2e"}`, borderRadius: 10,
    padding: "10px 11px", minWidth: 0, opacity: isDragging ? 0.4 : 1,
    boxShadow: "0 1px 0 rgba(0,0,0,0.3)", overflowWrap: "anywhere",
  };
}
